import { useBlocCoordinator } from "@lib/bloc/hooks"
import { useIsInIframe, useIsMobile, useLastNonNull } from "@lib/Hooks"
import { arrayOfNotNull, safeParseFloat, safeParseInt } from "@lib/TypeUtil"
import { WorkOrder, WorkOrderStatus, workOrderStatusForValue } from "@model/workOrders/WorkOrder"
import { Button, ButtonCont, ButtonTheme, ButtonType } from "@ui/common/buttons/Button"
import { DataField, EditableDataField } from "@ui/common/DataField"
import {
    TextInputDatePicker,
    TextInputDateTimePicker,
} from "@ui/common/form/DatePicker"
import {
    CollapsibleFormField,
    FormField,
    FormFieldDirection,
    FormFieldNote,
    FormFieldNoteType,
} from "@ui/common/form/FormField"
import { Select, SelectTheme } from "@ui/common/form/Select"
import { TextArea } from "@ui/common/form/TextArea"
import { TextInput, TextInputTheme, TextInputType } from "@ui/common/form/TextInput"
import { Grid, GridCont, GridGap } from "@ui/common/grids/Grid"
import { ImageUrls } from "@ui/common/images/ImageUrls"
import { LoadingIndicator, LoadingIndicatorContainer } from "@ui/common/LoadingIndicator"
import {
    Modal,
    ModalBody,
    ModalButtons,
    ModalContent,
    ModalHeader,
    ModalHeaderSubTitle,
    ModalHeaderTitle,
    ModalSize,
} from "@ui/common/Modal"
import { PageInfoSection } from "@ui/common/page/PageInfoSection"
import { DI } from "@ui/DI"
import React, { useEffect, useRef, useState } from "react"
import { Asset } from "@model/assets/Asset"
import { AssetLocation } from "@ui/workRequests/AssetLocation"
import { FetchDataErrorModal } from "@ui/common/FetchDataErrorModal"
import { Backdrop, Box, Chip, Divider, FormControlLabel, Icon, IconButton, Input, Paper, Switch, Typography } from "@material-ui/core"
import { ServiceItemsList } from "@ui/workRequests/ServiceItemsList"
import { AssetSearch } from "@ui/common/AssetSearch"
import { AssetIdentifierComponent } from "@ui/common/AssetIdentifier"
import { MechanicCard } from "@ui/common/MechanicCard"
import { ReassignWorkOrderModal } from "@ui/workOrders/ReassignWorkOrderModal"
import { WorkOrderNumber } from "@ui/workOrders/WorkOrderNumber"
import { CompleteWorkOrderModal } from "@ui/workOrders/CompleteWorkOrderModal"
import { ClosingWorkOrderModal } from "@ui/workOrders/ClosingWorkOrderModal"
import { PrintWorkOrderModal } from "@ui/workOrders/PrintWorkOrderModal"
import { PartsListModal } from "@ui/workOrders/PartsListModal"
import { MeterSyncInfoModal } from "@ui/workOrders/MeterSyncInfoModal"
import { PriorAssetUsage } from "@model/assets/PriorAssetUsage"
import { WorkOrderPrintOption } from "@model/workOrders/WorkOrderPrintOption"
import { OnHoldModal } from "@ui/workOrders/OnHoldModal"
import { Overlay } from "@ui/workOrders/Overlay"
import { HistoryTable } from "@ui/common/tables/HistoryTable"
import { WorkOrderEffect } from "@state/workOrders/CreateNewWorkOrderModalBloc"
import { SiteSearchCreate } from "@ui/common/SiteSearchCreate"
import { DismissWorkOrderModal } from "./DismissWorkOrderModal"
import { Link } from "@lib/router/components/Link"
import { ExternalRoutes, Routes } from "@lib/Routes"
import { MultiSelect } from "@ui/common/form/MultiSelect"
import { labelForContact } from "@model/contacts/Contact"
import { Strings } from "@lib/Strings"
import { MeterSyncModal } from "@ui/common/MeterSyncModal"
import { EmptyWorkOrderModal } from "@ui/workOrders/EmptyWorkOrderModal"
import { WorkHistoryOverlay } from "@ui/workOrders/WorkHistoryOverlay"
import { isRouteMatch } from "@lib/router/route"
import { getRedirectUrl } from "@model/RedirectData"
import { useRoute } from "@lib/router/hooks"

const IframeCaller = {createNewWorkOrderFromMobile : "createNewWorkOrderFromMobile"}

export function CreateNewWorkOrderModal({
    onWorkOrderDeleted,
    onWorkOrderCreated,
    onClose,
}: {
    onWorkOrderDeleted?: () => void,
    onWorkOrderCreated?: (workOrder: WorkOrder) => void,
    onClose?: (wasModified: boolean) => void
}): JSX.Element {
    const [state, viewModel] = useBlocCoordinator(() => DI.createNewWorkOrderScreenViewModel())

    return (
        <>
            <CreateNewWorkOrderDetailModal
                isVisible={state.isVisible}
                onClose={(wasModified) => {
                    viewModel.onCancelClicked()
                    onClose?.(wasModified)
                }}
                onWorkOrderDeleted={onWorkOrderDeleted}
            />
            <WorkOrderAfterSavedModal
                isModalVisible={state.isAfterSaveVisible}
                onClose={async () => {
                    await viewModel.onCloseAfterSaveClicked()
                    onClose?.(true)
                }}
                workOrderId={state.workOrder?.id ?? null}
                workOrderNumber={state.workOrder?.number ?? null}
                assetId={state.asset?.id ?? null}
                assetLabel={state.asset?.label ?? null}
            />
        </>
    )
}

export function CreateNewWorkOrderDetailModal({
    isVisible,
    onClose,
    onWorkOrderDeleted
}: {
    isVisible: boolean,
    onClose: (wasModified: boolean) => void,
    onWorkOrderDeleted?: () => void
}): JSX.Element {
    const isMobile = useIsMobile() || useIsInIframe()
    const redirectData = getRedirectUrl()
    const route = useRoute()
    const isCreateNewWorkOrderRoute = isRouteMatch(route, Routes.CreateNewWorkOrder)
    const isCreateNewWorkOrderForAssetRoute = isRouteMatch(route, Routes.CreateNewWorkOrderForAsset)
    const [state, viewModel] = useBlocCoordinator(() => DI.createNewWorkOrderScreenViewModel())
    const { assetId } = route.data.params

    useEffect(() => {

        if (isCreateNewWorkOrderRoute || isCreateNewWorkOrderForAssetRoute) {
            viewModel.showModal()
        }

        viewModel.onMounted()

        if (assetId && safeParseInt(assetId) != 0) {
            viewModel.loadDefaultSelectedAsset(safeParseInt(assetId))
        }

        if (route.data.query?.caller) {
            viewModel.callerFromMobileAppChanged(route.data.query.caller)
        }
    }, [])

    const modalSize = state.callerFromMobileApp != null && state.callerFromMobileApp === IframeCaller.createNewWorkOrderFromMobile ? ModalSize.Full : ModalSize.Large

    // Red trace highlighting for required fields (same behavior as the M2.0 WorkOrderScreen).
    // A field is highlighted only while it is editable and missing its required value.
    // Action Item #11164: one shared predicate for every closed-locked field (see canEditClosedLockedField).
    const isCompletionDateEditable: boolean = viewModel.isClosedLockedFieldEditable()
    const formCompletionDateInvalid: boolean =
        isCompletionDateEditable &&
        (state.form.completionDate == null ||
            viewModel.isServiceDateTimeOtsideExpectedRange(state.form.completionDate))
    const formDescriptionInvalid: boolean =
        viewModel.isEditionEnabled() && state.form.specialInstructions.trim().length === 0
    // Physical meters are required when the asset's displayHourMeter/displayOdometer property is true
    const formPhysicalHourMeterInvalid: boolean = viewModel.displayPhysicalHourmeterRequiredLegend() === true
    const formPhysicalOdometerInvalid: boolean = viewModel.displayPhysicalOdometerRequiredLegend() === true
    // Action Item #10448: fields the company selected as required on the Admin page
    const formAssignedToInvalid: boolean = viewModel.displayAssignedToRequiredLegend() === true
    const formSiteInvalid: boolean = viewModel.displaySiteRequiredLegend() === true

    return (
        <Modal isVisible={isVisible} useContentTag={true} onClose={() => { viewModel.onCloseClicked(); onClose(viewModel.hasBeenSaved()); }} size={modalSize}>
            <ModalContent>
                <ModalHeader>
                    <ModalHeaderTitle>
                        {state.isEditMode ? (state.workOrder?.number ? `Work Order ${state.workOrder.number}` : 'Work Order loading...') : 'Create Work Order'}
                    </ModalHeaderTitle>
                </ModalHeader>
                <ModalBody >
                    <LoadingIndicatorContainer>
                        <LoadingIndicator isLoading={state.isLoading || state.isEditModeLoading} />
                        <GridCont gap={GridGap.Small} className="create-new-work-order--grid-content">
                            <Grid columns={2}>
                                <Grid>
                                    {!state.isEditMode &&
                                        <AssetSearch
                                            onSelectedAssetChanged={viewModel.selectedAssetChanged}
                                            selectedAsset={state.asset}
                                            isEnabled={!assetId}
                                        />}
                                </Grid>
                                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px" }}>
                                    {state.asset &&
                                        <>
                                            {/* Action Item #6766: opens the read-only Work History Overlay */}
                                            <Button
                                                type={ButtonType.Icon}
                                                theme={ButtonTheme.Dark}
                                                iconUrl={ImageUrls.TableList}
                                                onClick={() => viewModel.viewWorkHistoryClicked()}
                                            />
                                            <Button
                                                type={ButtonType.Icon}
                                                theme={ButtonTheme.Dark}
                                                iconUrl={ImageUrls.WrenchSolid}
                                                isEnabled={
                                                    viewModel.hasWorkOrderEditPermission() &&
                                                    state.asset != null
                                                }
                                                onClick={() => { viewModel.viewPartsClicked() }}
                                            />
                                            <Button
                                                type={ButtonType.Icon}
                                                theme={ButtonTheme.Dark}
                                                iconUrl={ImageUrls.Print}
                                                isEnabled={
                                                    viewModel.hasWorkOrderEditPermission() &&
                                                    state.asset != null
                                                }
                                                onClick={() => { viewModel.printClicked() }}
                                            />
                                            {state.workOrder?.id &&
                                                <Button
                                                    type={ButtonType.Icon}
                                                    theme={ButtonTheme.Dark}
                                                    iconUrl={ImageUrls.Trash}
                                                    isEnabled={viewModel.canDeleteWorkOrder()}
                                                    onClick={() => { viewModel.dismissWorkOrderClicked() }}
                                                />}
                                        </>}
                                    <Button
                                        type={ButtonType.Contained}
                                        label={viewModel.saveButtonLabel()}
                                        isEnabled={viewModel.isSaveButtonEnabled()}
                                        onClick={viewModel.handleOnSaveButtonClicked}
                                    />
                                </div>
                            </Grid>
                            {state.asset && <>
                                <Grid columns={2}>
                                    <FormField label="Asset Details">
                                    </FormField>
                                </Grid>
                                <Divider></Divider>
                                <Grid columns={1}>
                                    <AssetInfoSection asset={state.asset} LatestAssetUsage={state.LatestAssetUsage} isMobile={isMobile} showOnHoldOverlay={((state.form?.onHold ?? false) && (state.form?.onHoldReason !== null && state.form?.onHoldReason !== ""))} OnHoldReason={state.form?.onHoldReason ?? null} />
                                </Grid>
                            </>}
                            <Grid columns={2}>
                                <FormField label="Work Order Details">
                                </FormField>
                            </Grid>
                            <Divider></Divider>
                            <Grid columns={3}>
                                <FormField label={"Work Order Status"}>
                                    <Select
                                        value={state.form?.status}
                                        options={viewModel.getWorkOrderStatusOptions()}
                                        isEnabled={viewModel.isClosedLockedFieldEditable()}
                                        onChange={viewModel.onStatusDropDownChange}
                                    />
                                </FormField>
                                <FormField label="Work Order Number">
                                    <WorkOrderNumber assetId={state.asset?.id ?? null} id={state.workOrder?.id ?? null} number={state.workOrder?.number ?? null} isLink={false} />
                                </FormField>
                                <div style={{ display: "flex", justifyContent: "flex-end", flexDirection: "column" }}>
                                    {/* temporary hidden */}
                                    {/* <FormControlLabel control={<UrgencySwitch checked={state.form.urgency} disabled={!viewModel.hasWorkOrderEditPermission() || state.form?.onHold} onChange={(event: React.ChangeEvent<HTMLInputElement>) => { viewModel.urgencyChanged(event.target.checked) }} color={true ? "secondary" : "default"} />} label="Mark as Urgent" labelPlacement="start" />
                                    <FormControlLabel control={<Switch checked={state.form.onHold ?? false} disabled={!viewModel.hasWorkOrderEditPermission()} onChange={(event: React.ChangeEvent<HTMLInputElement>) => { viewModel.onHoldChanged(event.target.checked) }} color="default" />} label="On Hold" labelPlacement="start" /> */}
                                </div>
                            </Grid>
                            <Grid columns={3}>
                                {state.form.createdByID || !state.isEditMode ?
                                    (state.isEditMode ? <>
                                        <MechanicCard label="Created By" mechanic={viewModel.getCreatedByDisplayData(state.form.createdByUserData)} />
                                    </> :
                                        <>
                                            <MechanicCard label="Created By" mechanic={viewModel.getContactsById(viewModel.getUser().personID)} />
                                        </>)
                                    : (<>
                                        <FormField label="Created By" direction={FormFieldDirection.Row}>
                                            <div style={{ minHeight: "43px", marginLeft: "10px" }}>
                                                <div style={{ fontWeight: "bold", fontSize: "14px" }}>Work Order Automation</div>
                                            </div>
                                        </FormField>
                                    </>)}
                                <MechanicCard label="Work Assigned To" mechanic={viewModel.getMechanicById(state.form.assignedTo)}
                                    isEditable={viewModel.isEditionEnabled()}
                                    onEdit={() => viewModel.setReassignWorkOrderModalIsVisible(true)}
                                    outlineInvalid={formAssignedToInvalid}
                                />
                                {formAssignedToInvalid && (
                                    <FormFieldNote type={FormFieldNoteType.Error}>
                                        Assigned To required
                                    </FormFieldNote>
                                )}
                                {/* Action Item 6767: Work Performed By moved to the Service Item
                                    level (see ServiceItem.tsx). The form field keeps round-tripping
                                    existing WO-level values invisibly so no saved Work Order loses
                                    data - absent UI must never mean wiped data. */}
                            </Grid>
                            {/* Action Item #7751: Row A — always visible: Work Order Due Date,
                                Due Hour Meter, Due Odometer, Site. The WO-level Due meters are
                                separate from the Service Item due meters and are adjustable at any
                                status. Site moved up here from its own row below. */}
                            <Grid columns={4}>
                                <FormField
                                    label={"Work Order Due Date"}
                                    direction={FormFieldDirection.Column}
                                >
                                    <TextInputDatePicker
                                        value={state.form.dueDate}
                                        onChange={(date) => viewModel.dueDateChanged(date)}
                                        disabled={
                                            !viewModel.isEditionEnabled()
                                        }

                                        disablePast={false}
                                    />
                                </FormField>
                                <FormField label="Due Hour Meter" direction={FormFieldDirection.Column}>
                                    <TextInput
                                        type={TextInputType.Decimal}
                                        min={0}
                                        value={state.form.dueHourMeter ?? ""}
                                        onChange={(value) => viewModel.dueHourMeterChanged(value)}
                                        isEnabled={viewModel.isEditionEnabled()}
                                    />
                                </FormField>
                                <FormField label="Due Odometer" direction={FormFieldDirection.Column}>
                                    <TextInput
                                        type={TextInputType.Integer}
                                        min={0}
                                        value={state.form.dueOdometer ?? ""}
                                        onChange={(value) => viewModel.dueOdometerChanged(value)}
                                        isEnabled={viewModel.isEditionEnabled()}
                                    />
                                </FormField>
                                <FormField label={"Site"} className="site-form-field">
                                    {viewModel.hasSiteAddPermission() ?
                                        <SiteSearchCreate isEnabled={viewModel.isEditionEnabled()} selectedSite={state.form?.site} onSelectedSiteChanged={(site) => viewModel.selectedSiteChanged(site)} outlineInvalid={formSiteInvalid}></SiteSearchCreate>
                                        : <Select
                                            theme={SelectTheme.Normal}
                                            className={formSiteInvalid ? "outline-invalid" : undefined}
                                            options={[
                                                { label: "Select", value: "" },
                                                ...state.sites.map((it) => ({
                                                    label: it.name,
                                                    value: it.id.toString(),
                                                })),
                                            ]}
                                            value={state.form?.site?.id ?? ""}
                                            isEnabled={viewModel.isEditionEnabled()}
                                            onChange={(value) => viewModel.selectedSiteChanged(state.sites.find((it) => it.id.toString() === value) ?? null)}
                                        ></Select>}
                                    {formSiteInvalid && (
                                        <FormFieldNote type={FormFieldNoteType.Error}>
                                            Site required
                                        </FormFieldNote>
                                    )}
                                </FormField>
                            </Grid>
                            {/* Action Item #7751: Row B — only visible once the WO is in Work
                                Completed (or Closed): Completion Date & Time, then Physical Hour
                                Meter + Physical Odometer to its right. Physical fields were
                                previously always visible; they now follow the same status gate
                                as the Completion Date field.
                                Uses columns={4} (matching Row A) so the fields stack cleanly:
                                Completion Date under Due Date, Physical Hour Meter under Due Hour
                                Meter, Physical Odometer under Due Odometer, with the 4th column
                                (under Site) left empty. */}
                            {(state.form.status === WorkOrderStatus.WorkCompleted || state.form.status === WorkOrderStatus.Closed) &&
                                <Grid columns={4}>
                                    <FormField label={"Completion Date & Time*"} direction={FormFieldDirection.Column}>
                                        <TextInputDateTimePicker
                                            value={state.form.completionDate}
                                            onChange={(date) => viewModel.completionDateChanged(date)}
                                            disabled={!isCompletionDateEditable}
                                            disableFuture={true}
                                            disablePast={false}
                                            outlineInvalid={formCompletionDateInvalid}
                                        />
                                        {state.form.completionDate &&
                                            viewModel.isServiceDateTimeOtsideExpectedRange(
                                                state.form.completionDate
                                            ) ? (
                                            <FormFieldNote type={FormFieldNoteType.Error}>
                                                Service time cannot be in the future
                                            </FormFieldNote>
                                        ) : (
                                            ""
                                        )}
                                    </FormField>
                                    <FormField
                                        className="PhysicalMeter"
                                        label={<>Physical Hour Meter {state.asset?.label?.endsWith("+") && (<Button
                                            type={ButtonType.Icon}
                                            theme={ButtonTheme.Light}
                                            iconUrl={ImageUrls.Info}
                                            onClick={() => viewModel.meterSyncInfoClicked()}
                                            tooltip="Automatic Meter Syncs"
                                            className="MeterSyncInfo-small"
                                            tabIndex={-1}
                                        />)}</>}
                                        direction={FormFieldDirection.Column}
                                    >
                                        <TextInput
                                            type={TextInputType.Decimal}
                                            min={0}

                                            isEnabled={viewModel.isClosedLockedFieldEditable()}
                                            value={state.form.physicalHourMeter ?? ""}
                                            onChange={(value) => viewModel.physicalHourMeterChanged(value)}
                                            outlineInvalid={formPhysicalHourMeterInvalid}
                                        />
                                        {state.form.completionDate != null && state.asset?.displayHourMeter &&
                                            <FormFieldNote>
                                                Smart Hub Hour Meter:{" "}
                                                {safeParseFloat(state.AssetUsagePriorToCompletion?.assetReading.hourMeter ?? state.LatestAssetUsage?.assetReading.hourMeter).toFixed(1)}
                                            </FormFieldNote>
                                        }

                                        {viewModel.displayPhysicalHourmeterRequiredLegend() && (
                                            <FormFieldNote type={FormFieldNoteType.Error}>
                                                Physical Hour Meter required
                                            </FormFieldNote>
                                        )}

                                    </FormField>
                                    <FormField
                                        className="PhysicalMeter"
                                        label={<>Physical Odometer {state.asset?.label?.endsWith("+") && (<Button
                                            type={ButtonType.Icon}
                                            theme={ButtonTheme.Light}
                                            iconUrl={ImageUrls.Info}
                                            onClick={() => viewModel.meterSyncInfoClicked()}
                                            tooltip="Automatic Meter Syncs"
                                            className="MeterSyncInfo-small"
                                            tabIndex={-1}
                                        />)}</>}
                                        direction={FormFieldDirection.Column}
                                    >
                                        <TextInput
                                            type={TextInputType.Integer}
                                            min={0}
                                            isEnabled={viewModel.isClosedLockedFieldEditable()}
                                            value={state.form.physicalOdometer ?? ""}
                                            onChange={(value) => viewModel.physicalOdometerChanged(value)}
                                            outlineInvalid={formPhysicalOdometerInvalid}
                                        />
                                        {state.form.completionDate != null && state.asset?.displayOdometer &&
                                            <FormFieldNote>
                                                Smart Hub Odometer:{" "}
                                                {safeParseInt(state.AssetUsagePriorToCompletion?.assetReading.odometer ?? state.LatestAssetUsage?.assetReading.odometer)}
                                            </FormFieldNote>
                                        }
                                        {viewModel.displayPhysicalOdometerRequiredLegend() && (
                                            <FormFieldNote type={FormFieldNoteType.Error}>
                                                Physical Odometer required
                                            </FormFieldNote>
                                        )}
                                    </FormField>
                                </Grid>
                            }
                            <Grid columns={1}>
                                <FormField label="Description*" direction={FormFieldDirection.Column}>
                                    <TextArea
                                        title="Description*"
                                        maxLength={1000}
                                        // className="create-new-work-order--text-area"
                                        placeholder={"Describe the issue or provide detailed Instructions for the technician."}
                                        value={state.form.specialInstructions}
                                        disabled={
                                            !viewModel.isEditionEnabled()
                                        }
                                        onChange={(value) => viewModel.specialInstructionsChanged(value)}
                                        outlineInvalid={formDescriptionInvalid}
                                    />
                                </FormField>
                            </Grid>
                            <Grid columns={1}>
                                <ServiceItemsList isEnabled={viewModel.isEditionEnabled()} asset={state.asset} SaveButton={<Button
                                    type={ButtonType.Contained}
                                    label={viewModel.saveButtonLabel()}
                                    isEnabled={viewModel.isSaveButtonEnabled()}
                                    onClick={viewModel.handleOnSaveButtonClicked}
                                />} isClosedWorkOrder={state.workOrder?.status === WorkOrderStatus.Closed} />
                            </Grid>
                            {state.isEditMode &&
                                <CollapsibleFormField
                                    label="Work Order History"
                                    isLoading={viewModel.state.effectStatus[WorkOrderEffect.FetchWorkOrderHistory].isBusy()}
                                    onClick={() => {
                                        if (state.workOrder) viewModel.loadWorkOrderHistory(state.workOrder?.id)
                                    }}
                                    isCollapsed={true}
                                >
                                    <HistoryTable history={state.workOrderHistory} statusLabelCreator={(x) => x} />
                                </CollapsibleFormField>
                            }
                        </GridCont>
                    </LoadingIndicatorContainer>
                    <FetchDataErrorModal
                        title="Error Loading Work Order"
                        message="There was an error loading the work order. Please retry or return to the work orders list."
                        buttonLabel="View Work Orders"
                        isVisible={state.hasDataFetchError}
                        onClick={() => viewModel.viewWorkOrdersClicked()}
                        onRetry={() => viewModel.onMounted()}
                    />
                    <ReassignWorkOrderModal
                        mechanics={state.mechanics}
                        selectedMechanic={state.form.assignedTo}
                        dueDate={state.form.dueDate}
                        isModalVisible={state.ReassignWorkOrderModalIsVisible}
                        onClose={() => viewModel.setReassignWorkOrderModalIsVisible(false)}
                        OnSendWorkOrderConfirmed={(mechanicId, dueDate) => viewModel.handleReassignWorkOrderClicked(mechanicId ?? 0, dueDate)}
                    />
                    <CompleteWorkOrderModal
                        isVisible={viewModel.state.completeModalOpen}
                        onClose={() => {
                            viewModel.setCompleteModalOpen(false)
                        }}
                        serviceItems={viewModel.getServiceItems()}
                        missingFieldsByItem={viewModel.getServiceItemsMissingRequiredFields()}
                        onConfirm={(completionDate, updatedItems) => {
                            state.form.status = WorkOrderStatus.WorkCompleted
                            viewModel.statusChanged(workOrderStatusForValue(WorkOrderStatus.WorkCompleted))
                            console.log(updatedItems)
                            viewModel.updateServiceItems(updatedItems)
                            viewModel.completionDateChanged(completionDate)
                            viewModel.setCompleteModalOpen(false)
                        }}
                    />
                    <ClosingWorkOrderModal
                        isVisible={viewModel.state.closingModalOpen}
                        onClose={() => {
                            viewModel.setClosingModalOpen(false)
                        }}
                        serviceItems={viewModel.getServiceItems()}
                        missingFieldsByItem={viewModel.getServiceItemsMissingRequiredFields()}
                        asset={state.asset}
                        initialCompletionDate={state.form.completionDate}
                        initialPhysicalHourMeter={state.form.physicalHourMeter}
                        initialPhysicalOdometer={state.form.physicalOdometer}
                        smartHubHourMeter={safeParseFloat(state.AssetUsagePriorToCompletion?.assetReading.hourMeter ?? state.LatestAssetUsage?.assetReading.hourMeter).toFixed(1)}
                        smartHubOdometer={safeParseInt(state.AssetUsagePriorToCompletion?.assetReading.odometer ?? state.LatestAssetUsage?.assetReading.odometer).toString()}
                        onConfirm={(completionDate, updatedItems, physicalHourMeter, physicalOdometer) => {
                            // Update the service items first: their change listener recalculates the
                            // automated status, which would overwrite Closed with Work Completed.
                            viewModel.updateServiceItems(updatedItems)
                            viewModel.completionDateChanged(completionDate)
                            viewModel.physicalHourMeterChanged(physicalHourMeter)
                            viewModel.physicalOdometerChanged(physicalOdometer)
                            viewModel.statusChanged(WorkOrderStatus.Closed)
                            viewModel.setClosingModalOpen(false)
                        }}
                    />
                    <PrintWorkOrderModal
                        isVisible={state.showPrintList}
                        isLoading={state.workOrderToPrintIsLoading}
                        onClose={() => viewModel.hidePrintList()}
                        onOptionSelected={(option) => viewModel.workOrderPrintOptionSelected(option)}
                        companyName={state.pdfCompanyName}
                        workOrder={state.pdfWorkOrder}
                        assetParts={state.pdfAssetParts}
                        asset={state.pdfAsset}
                        selectedPrintOption={state.selectedPrintOption}
                        companyLogoUrl={state.companyLogoUrl}
                        optionsEnabled={{
                            [WorkOrderPrintOption.WorkOrder]: state.workOrder != null && state.asset != null,
                            [WorkOrderPrintOption.Summary]: state.workOrder != null && state.asset != null,
                            [WorkOrderPrintOption.Parts]: state.asset != null,
                            [WorkOrderPrintOption.WorkOrderWithParts]: state.workOrder != null && state.asset != null,
                            [WorkOrderPrintOption.SummaryWithParts]: state.workOrder != null && state.asset != null,
                        }}
                    />
                    <PartsListModal
                        isLoading={state.partsListIsLoading}
                        isVisible={state.showPartsList}
                        onClose={() => viewModel.partListViewCloseButtonClicked()}
                        parts={state.AssetParts}
                    />
                    <MeterSyncModal
                        isVisible={state.showSyncMeter}
                        onCancelClick={() => viewModel.meterSyncCancelClicked()}
                        onCloseWithoutSyncClick={() => viewModel.meterSyncCloseWithoutSyncClicked()}
                        onPerformSyncClick={() => viewModel.meterSyncPerformClicked()}
                    />
                    <EmptyWorkOrderModal
                        isVisible={state.showEmptyWorkOrderModal}
                        doNotShowAgain={state.emptyWorkOrderModalDoNotShowAgain}
                        onDoNotShowAgainChanged={(isChecked) => viewModel.emptyWorkOrderDoNotShowAgainChanged(isChecked)}
                        onCancelClick={() => viewModel.emptyWorkOrderCancelClicked()}
                        onConfirmClick={() => viewModel.emptyWorkOrderConfirmClicked()}
                    />
                    <MeterSyncInfoModal
                        isVisible={state.showMeterSyncInfo}
                        onClose={() => viewModel.meterSyncInfoCloseButtonClicked()}
                    />
                    <OnHoldModal
                        isVisible={state.showOnHoldModal}
                        onClose={() => viewModel.showOnHoldModalChanged(false)}
                        onHoldChanged={(reason) => { viewModel.onHoldReasonChanged(reason); viewModel.onHoldChanged(true); }}
                    />

                    <DismissWorkOrderModal
                        data={
                            state.workOrderDismissal === null
                                ? null
                                : {
                                    workRequests: state.workRequests,
                                    reason: state.workOrderDismissal.reason,
                                    workRequestsToDelete: state.workOrderDismissal.workRequestsToDelete ?? []
                                }
                        }
                        onCancel={() => viewModel.dismissWorkOrderCancelClicked()}
                        onConfirm={async (reason, requestToDismiss) => {
                            await viewModel.dismissWorkOrderConfirmClicked(reason, requestToDismiss)
                            onWorkOrderDeleted?.()
                        }
                        }
                        onReasonChanged={(value) => viewModel.dismissWorkOrderReasonChanged(value)}
                        onWorkRequestsToDismissChanged={(requestId, shouldDismiss) =>
                            viewModel.dismissWorkOrderWorkRequestsToDismissChanged(requestId, shouldDismiss)
                        }
                        hasDismissWorkRequestPermission={viewModel.hasWorkRequestDeletePermission()}
                        deleteAllServiceItems={viewModel.isClosedWorkOrder()}
                        itemDescriptionTerminology={"Service Item"}
                        dismissActionDescription={"Delete"}
                        returnToTheQueueActionDescription={"Decline"}
                        customPreventativeAndInspectionServicesMessage={"The following scheduled service items will be declined back to the service item queue."}
                        customServiceRepairMessage={"The following service items can be deleted or declined back to the service item queue."}
                    />
                    {/* Action Item #6766: read-only work history. It sits on top of this form and
                        has to be closed before the form can be edited again. */}
                    <WorkHistoryOverlay />
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

export function WorkOrderAfterSavedModal({
    isModalVisible,
    onClose,
    workOrderId,
    workOrderNumber,
    assetId,
    assetLabel
}: {
    isModalVisible: boolean,
    onClose: () => Promise<void>,
    workOrderId: number | null,
    workOrderNumber: string | null,
    assetId: number | null
    assetLabel?: string | null,
}): JSX.Element {

    return (
        <Modal isVisible={isModalVisible} useContentTag={true} onClose={async () => await onClose()} size={ModalSize.Normal}>
            <ModalContent>
                <ModalHeader>
                    <ModalHeaderTitle>
                        Work Order {workOrderNumber} Saved
                    </ModalHeaderTitle>
                </ModalHeader>
                <ModalBody>
                    <Grid columns={1} gap={GridGap.Small}>
                        <a href={ExternalRoutes.SmartHubAsset(assetId ?? 0)} target="_blank" style={{ textDecoration: 'none' }}>
                            {assetLabel}
                        </a>
                        <br></br>
                    </Grid>
                    <Grid columns={1} gap={GridGap.Small}>
                        <Link to={Routes.NewWorkOrder(workOrderId != null ? workOrderId.toString() : "", assetId != null ? assetId.toString() : "")} onClick={async () => await setTimeout(() => onClose(), 1000)}>View Work Order</Link>
                    </Grid>
                </ModalBody>
                <Divider></Divider>
                <ModalButtons>
                    <Button label={"Close"} onClick={async () => await onClose()} />
                </ModalButtons>
            </ModalContent>
        </Modal>)
}


export function AssetInfoSection({ asset, LatestAssetUsage, isMobile, showOnHoldOverlay, OnHoldReason }: { asset: Asset | null, LatestAssetUsage: PriorAssetUsage | null, isMobile: boolean, showOnHoldOverlay: boolean, OnHoldReason: string | null }): JSX.Element {

    return (
        <Box sx={{ position: 'relative' }}>
            <PageInfoSection>
                <Grid columns={1}>
                    <AssetIdentifierComponent asset={asset} />
                    <Grid columns={5}>
                        <DataField
                            label="Lifetime Hours"
                            value={safeParseFloat(asset?.lifetimeHourMeter).toFixed(1)}
                        />
                        <DataField
                            label="Current Hours"
                            value={safeParseFloat(asset?.hourMeter).toFixed(1)}
                        />
                        <DataField
                            label="Current Miles"
                            value={safeParseInt(asset?.odometer)}
                        />
                        <DataField
                            label="Last Update"
                            value={LatestAssetUsage?.eventDateTime ? Strings.dateTimeFormatter(LatestAssetUsage.eventDateTime, LatestAssetUsage.timeZone?.Abbreviation ?? "") : "N/A"}
                        />
                        <DataField
                            label="Last Known Location"
                            value={
                                <AssetLocation
                                    location={asset?.location ?? null}
                                    site={asset?.site ?? null}
                                    hasLinks={false}
                                />
                            }
                        />
                    </Grid>
                    <Overlay isVisible={showOnHoldOverlay} legend={OnHoldReason} />
                </Grid>
            </PageInfoSection>
        </Box>
    )
}


